import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { PAYMENT_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${PAYMENT_SERVICE_BASE_URL}/api/v1/charges`;
const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 1000;

export default function useChargeConfigs() {
  const authToken = sessionStorage.getItem("authToken");

  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // error handler
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      const status = err.response.status;
      if (status >= 500 && status < 600) {
        message = "Something went wrong. Please try again later.";
      } else if (status === 400 || status === 401) {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      } else {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      }
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // fetch all charges (Paged)
  const fetchAllCharges = useCallback(
    async (page = DEFAULT_PAGE, size = DEFAULT_SIZE) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          size: String(size),
        });

        const res = await apiClient.get(`${BASE_URL}?${params.toString()}`);

        const content = Array.isArray(res.data?.content)
          ? res.data.content
          : [];

        setCharges(content);
        setTotalPages(res.data?.totalPages || 1);
        setTotalElements(res.data?.totalElements || content.length || 0);
      } catch (err) {
        handleAxiosErrors(err, "fetch charges");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken],
  );

  // fetch charge by ID
  const fetchChargeById = useCallback(
    async (chargeId) => {
      if (!authToken) return;
      try {
        const res = await apiClient.get(`${BASE_URL}/${chargeId}`);
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch charge by id");
      }
    },
    [authToken],
  );

  // fetch effective charges paged:
  const fetchEffectiveCharges = useCallback(
    async ({
      app,
      stages = [],
      currency = "USD",
      country = "US",
      state,
      county,
      city,
      page = 0,
      size = 20,
    }) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          app,
          currency,
          page: String(page),
          size: String(size),
        });

        if (country) params.append("country", country);
        if (state) params.append("state", state);
        if (county) params.append("county", county);
        if (city) params.append("city", city);
        
        stages.forEach((s) => params.append("stages", s));

        const res = await apiClient.get(
          `${BASE_URL}/effective/paged?${params.toString()}`,
        );
        const content = Array.isArray(res.data?.content)
          ? res.data.content
          : [];
        setCharges(content);
        setTotalPages(res.data?.totalPages || 1);
        setTotalElements(res.data?.totalElements || content.length);

        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch effective charges");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // create charge
  const createCharge = useCallback(
    async (payload) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const res = await apiClient.post(`${BASE_URL}`, payload);
        console.log("response", res.data);
        toast.success("Charge created successfully");
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "create charge");

        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // update charge
  const updateCharge = useCallback(
    async (chargeId, payload) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const res = await apiClient.put(`${BASE_URL}/${chargeId}`, payload);
        toast.success("Charge updated successfully");
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "update charge");

        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // toggle status
  const toggleChargeStatus = useCallback(
    async (chargeId, status) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const res = await apiClient.patch(
          `${BASE_URL}/${chargeId}/status`,
          null,
          {
            params: { status },
          },
        );
        toast.success(
          `Charge ${status === "ACTIVE" ? "activated" : "deactivated"}`,
        );
        // Update local state
        setCharges((prev) =>
          prev.map((c) =>
            c.id === chargeId ? { ...c, status: res.data.status } : c,
          ),
        );
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "toggle charge status");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // delete charge
  const deleteCharge = useCallback(
    async (chargeId) => {
      if (!authToken) return;
      setLoading(true);
      try {
        await apiClient.delete(`${BASE_URL}/${chargeId}`);
        setCharges((prev) => prev.filter((c) => c.id !== chargeId));
        toast.success("Charge deleted successfully");
      } catch (err) {
        handleAxiosErrors(err, "delete charge");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // search/filter charges
  const searchCharges = useCallback(
    async (filters = {}, page = DEFAULT_PAGE, size = DEFAULT_SIZE) => {
      if (!authToken) return;
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: String(page),
          size: String(size),
        });

        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.append(key, value);
          }
        });

        const endpoint =
          Object.keys(filters).length > 0 ? `${BASE_URL}/search` : BASE_URL;

        const res = await apiClient.get(`${endpoint}?${params.toString()}`);
        const content = Array.isArray(res.data?.content)
          ? res.data.content
          : [];
        setCharges(content);
        setTotalPages(res.data?.totalPages || 1);
        setTotalElements(res.data?.totalElements || content.length || 0);
      } catch (err) {
        handleAxiosErrors(err, "search charges");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // auto fetch on mount
  useEffect(() => {
    if (authToken) fetchAllCharges();
  }, [authToken, fetchAllCharges]);

  return {
    charges,
    loading,
    error,
    initialLoad,
    totalPages,
    totalElements,
    fetchAllCharges,
    fetchChargeById,
    fetchEffectiveCharges,
    createCharge,
    updateCharge,
    toggleChargeStatus,
    deleteCharge,
    searchCharges,
  };
}
